import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Prepare the pay link BEFORE the buyer presses pay, so the pay control can be
 * a real `<a href>`.
 *
 * ⚠️ Since 2026-10-01 what `create` prepares is a LINK to our own API
 * (`prepareCheckoutLink`), not an order: the order is written when the buyer
 * follows it, so nobody who only looked leaves a `pending` row or a server
 * `checkout_started` behind. The hook does not care which — it takes
 * `{ redirectUrl, checkoutId }` either way; `checkoutId` is then the id the
 * link RESERVED for the order. The history below is why a link at all.
 *
 * ## Why this exists
 *
 * Measured on production 19–22.09.2026: hosted one-time payments fell from 12
 * completions of 36 starters to 3 of 21, and in the 24 h to 22.09 **12 orders
 * were created and 0 completed** — with most buyers never reaching the provider
 * page at all, on iPhone Chrome and on desktop as well as inside Instagram.
 *
 * The mechanism is the order of operations, not the gateway. The old flow was
 * `tap → await POST /billing/checkout → window.location.assign(redirectUrl)`.
 * That `assign` runs from an async callback, i.e. OUTSIDE the tap's user-gesture
 * window, and a browser that refuses such a navigation refuses it SILENTLY — no
 * throw, no event, nothing to catch or report. The buyer is left looking at a
 * sheet that did nothing, and taps again, which is why orders per buyer rose
 * from 1.4 to 2.6 while completions went to zero.
 *
 * An anchor has no such problem: following a link IS the gesture. So the order
 * is created in the background as soon as we know (a) which gateway and (b) who
 * the buyer is, and the pay control becomes an anchor to the URL that order
 * returned. Nothing about the navigation is then ours to get wrong.
 *
 * ## What it deliberately does NOT do
 *
 * It never creates an order for a buyer we cannot bill — `payment_orders.user_id`
 * is NOT NULL, so `identified` gates everything. And it never retries by itself:
 * a failure hands the sheet back its ordinary create-then-navigate button, which
 * is the path that still works everywhere and is what the buyer gets today.
 *
 * ## The cost, and who paid it (22.09 – 01.10)
 *
 * While `create` made the order itself, orders existed for buyers who never
 * pressed pay, sitting `pending` until the 45-minute abandoned-checkout sweep
 * saw them; the sweep records those as `checkout_abandoned{reason:'never_opened'}`
 * and alerts nobody. With the link, such orders no longer exist — the filter
 * stays for the few made by the direct POST fallback and by stale tabs.
 */
export type PreparedCheckout =
  /** No gateway picked yet, or nobody to bill — the ordinary button stands. */
  | { status: 'idle' }
  /** The order is being created. The pay control is non-interactive meanwhile. */
  | { status: 'preparing' }
  /** Ready: `url` is the provider page, `orderId` the row it belongs to. */
  | { status: 'ready'; url: string; orderId: string }
  /** Creation failed. The ordinary create-then-navigate button comes back. */
  | { status: 'failed' };

export interface PreparedCheckoutApi {
  state: PreparedCheckout;
  /** Forget everything — for a sheet that closes, or a buyer that changes. */
  reset: () => void;
}

export function usePreparedCheckout(opts: {
  /** The sheet is on screen. False parks the hook and clears nothing. */
  enabled: boolean;
  /**
   * Which gateway the buyer picked, or `null` while they have not. Doubles as
   * the cache key: toggling Payme → Click → Payme re-uses the first order
   * instead of minting a third.
   */
  gateway: string | null;
  /** The buyer can own an order: a member, or a guest whose checkout token exists. */
  identified: boolean;
  /** Creates the order. Its identity may change every render; it is read from a ref. */
  create: (gateway: string) => Promise<{ redirectUrl: string; checkoutId: string }>;
  /**
   * Ran once per newly created order, before anything is rendered as ready.
   * This is where a guest's session is adopted (`identify.commit()`) and where
   * `checkout_handoff_shown` is emitted — both must happen while the page is
   * certainly still here, not racing the navigation the anchor will start.
   */
  onReady?: (order: { url: string; orderId: string; gateway: string }) => void;
}): PreparedCheckoutApi {
  const { enabled, gateway, identified } = opts;
  const [state, setState] = useState<PreparedCheckout>({ status: 'idle' });
  /** gateway → the order already created for it, so a toggle costs nothing. */
  const cache = useRef(new Map<string, { url: string; orderId: string }>());
  /** Which gateway a creation is in flight for — one at a time, no races. */
  const inFlight = useRef<string | null>(null);
  const createRef = useRef(opts.create);
  const onReadyRef = useRef(opts.onReady);
  createRef.current = opts.create;
  onReadyRef.current = opts.onReady;

  const reset = useCallback(() => {
    cache.current.clear();
    inFlight.current = null;
    setState({ status: 'idle' });
  }, []);

  useEffect(() => {
    if (!enabled || !gateway || !identified) {
      // Not a reset: a buyer who deselects (or whose sheet re-renders before
      // identification) keeps the order already made for them.
      if (!gateway) setState({ status: 'idle' });
      return;
    }
    const hit = cache.current.get(gateway);
    if (hit) {
      setState({ status: 'ready', ...hit });
      return;
    }
    if (inFlight.current === gateway) return;
    inFlight.current = gateway;
    setState({ status: 'preparing' });
    let cancelled = false;
    // `Promise.resolve().then(create)` rather than `create(…).then(…)`: a
    // synchronous throw inside `create` must land in the rejection branch below
    // and hand the sheet its ordinary button back, not escape as an unhandled
    // error from an effect nobody is catching.
    void Promise.resolve()
      .then(createRef.current.bind(null, gateway))
      .then(
        (result) => {
          inFlight.current = null;
          // A response without a URL (a stubbed client, a provider that answered
          // with nothing) is a failed preparation, not a crash: the sheet gets
          // its ordinary button back. Thrown here it would be an UNHANDLED
          // rejection — the rejection branch below does not cover this handler.
          if (!result?.redirectUrl) {
            if (!cancelled) setState({ status: 'failed' });
            return;
          }
          const order = { url: result.redirectUrl, orderId: result.checkoutId };
          // Cached even if this effect was torn down: the order EXISTS server-side
          // now, and re-selecting the same gateway must re-use it rather than
          // create a second one nobody will pay.
          cache.current.set(gateway, order);
          if (cancelled) return;
          onReadyRef.current?.({ ...order, gateway });
          setState({ status: 'ready', ...order });
        },
        () => {
          inFlight.current = null;
          if (cancelled) return;
          // No retry loop. The sheet falls back to the button it always had, and
          // that button's own error line says what happened.
          setState({ status: 'failed' });
        },
      )
      .catch(() => {
        inFlight.current = null;
        if (!cancelled) setState({ status: 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, gateway, identified]);

  return { state, reset };
}
